import { useCallback, useEffect, useState } from 'react';
import type { FormEvent } from 'react';

import { categorySuggestions, isPeriod, sortEvents } from '@loggy/shared';
import type { EventInput, LifeEvent } from '@loggy/shared';

import {
  Alert,
  Badge,
  Button,
  Card,
  Field,
  Heading,
  Inline,
  Input,
  Spinner,
  Stack,
  Text,
  Textarea,
} from '../design/index.js';
import { createNodeEvent, deleteNodeEvent, fetchNodeEvents, updateNodeEvent } from '../api.js';
import styles from './LifeEvents.module.css';

export interface LifeEventsProps {
  nodeId: string;
  /** True when viewing someone else's dashboard. */
  readOnly: boolean;
  /** Called after a create, edit or delete lands. */
  onChanged?: () => void;
}

type ListState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'loaded'; events: LifeEvent[] };

/** Which row, if any, is being added or edited. Only one at a time — editing
 *  a second row while the first has unsaved changes would need to decide
 *  what happens to those, and nothing in the brief asks for that. */
type FormState = { kind: 'closed' } | { kind: 'create' } | { kind: 'edit'; event: LifeEvent };

/** Which row, if any, is mid-delete. Same one-at-a-time reasoning as above. */
type DeleteState = { id: string; kind: 'confirm' | 'deleting' } | null;

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback;
}

/**
 * Renders an event's date in UTC, unconditionally.
 *
 * `startsAt`/`endsAt` are always midnight or end-of-day UTC of the calendar
 * date the owner picked (see `toStartInstant`/`toEndInstant` below) — reading
 * them back in the browser's local zone would show the day before to anyone
 * west of UTC. For "the day my mother died" that is not an acceptable
 * rounding error, so every render goes through this and only this.
 */
function formatEventDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    timeZone: 'UTC',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatEventRange(event: LifeEvent): string {
  if (isPeriod(event) && event.endsAt) {
    return `${formatEventDate(event.startsAt)} – ${formatEventDate(event.endsAt)}`;
  }
  return formatEventDate(event.startsAt);
}

/** A calendar date (`YYYY-MM-DD`, straight off `<input type="date">`) becomes
 *  the first instant of that day in UTC. */
function toStartInstant(date: string): string {
  return `${date}T00:00:00.000Z`;
}

/** ...and the end date becomes the LAST instant of ITS day, so a period
 *  covers the whole of its final day rather than stopping at its start. */
function toEndInstant(date: string): string {
  return `${date}T23:59:59.999Z`;
}

/** Id shared between the category `<input list>` and its `<datalist>`. Only
 *  one form is ever open at a time (see `FormState`), so one fixed id never
 *  collides with itself. */
const CATEGORY_OPTIONS_ID = 'life-event-category-options';

/**
 * Add-an-event / edit-an-event, in one form — filling in the end date is
 * what makes it a period, rather than a separate moment/period switch.
 */
function EventForm({
  event,
  categoryOptions,
  onSaved,
  onCancel,
  createEvent,
}: {
  event: LifeEvent | null;
  /** Categories already used, in the owner's own spelling, plus the app's
   *  suggestions — see `categorySuggestions` in '@loggy/shared'. */
  categoryOptions: string[];
  onSaved: (event: LifeEvent) => void;
  onCancel: () => void;
  createEvent: (input: EventInput) => Promise<LifeEvent>;
}) {
  const isEditing = event != null;

  const [title, setTitle] = useState(event?.title ?? '');
  // Slicing the already-UTC ISO string, exactly as GoalForm does for a
  // goal's deadline — never round-tripping through `new Date(...)`, which is
  // how the timezone bug gets in.
  const [startDate, setStartDate] = useState(event ? event.startsAt.slice(0, 10) : '');
  const [endDate, setEndDate] = useState(event?.endsAt ? event.endsAt.slice(0, 10) : '');
  const [note, setNote] = useState(event?.note ?? '');
  const [category, setCategory] = useState(event?.category ?? '');

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  async function handleSubmit(formEvent: FormEvent<HTMLFormElement>) {
    formEvent.preventDefault();
    if (submitting) return;

    const trimmedTitle = title.trim();
    if (!trimmedTitle || !startDate) {
      setSubmitError('Fill in a title and a date.');
      return;
    }
    if (endDate && endDate < startDate) {
      setSubmitError('The end date can’t be before the start date.');
      return;
    }

    const input: EventInput = {
      startsAt: toStartInstant(startDate),
      title: trimmedTitle,
    };
    if (endDate) input.endsAt = toEndInstant(endDate);
    if (note.trim()) input.note = note.trim();
    // Store exactly what was typed — no normalizing here. An empty field sends
    // no `category` at all rather than an empty string, same as `note` above.
    if (category.trim()) input.category = category.trim();

    setSubmitting(true);
    setSubmitError(null);
    try {
      const saved = await createEvent(input);
      onSaved(saved);
    } catch (err) {
      setSubmitting(false);
      setSubmitError(errorMessage(err, 'Could not save this event.'));
    }
  }

  return (
    <Card data-testid={isEditing ? 'life-event-form-edit' : 'life-event-form-create'}>
      <form onSubmit={handleSubmit} noValidate>
        <Stack gap={5}>
          <Heading level={4} size="md">{isEditing ? 'Edit event' : 'Add an event'}</Heading>

          <Field label="Title" required>
            <Input
              data-testid="life-event-form-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Two weeks in Portugal"
              disabled={submitting}
              required
            />
          </Field>

          <Inline gap={4} wrap className={styles.dateRow}>
            <Field label="Start date" required className={styles.dateField}>
              <Input
                data-testid="life-event-form-start"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                disabled={submitting}
                required
              />
            </Field>

            <Field
              label="End date (optional)"
              hint="Leave this blank for something that happened on a single day."
              className={styles.dateField}
            >
              <Input
                data-testid="life-event-form-end"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                disabled={submitting}
              />
            </Field>
          </Inline>

          <Field label="Note (optional)">
            <Textarea
              data-testid="life-event-form-note"
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Whatever is worth remembering about it"
              disabled={submitting}
            />
          </Field>

          <Field
            label="Category (optional)"
            hint="Suggests what you've used before — type anything, it's kept as you write it."
          >
            <Input
              data-testid="life-event-form-category"
              list={CATEGORY_OPTIONS_ID}
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              placeholder="e.g. Holiday"
              disabled={submitting}
            />
          </Field>
          <datalist id={CATEGORY_OPTIONS_ID}>
            {categoryOptions.map((option) => (
              <option key={option} value={option} />
            ))}
          </datalist>

          {submitError && (
            <Alert variant="error" title="Couldn't save this event" data-testid="life-event-form-error">
              {submitError}
            </Alert>
          )}

          <Inline gap={3} align="center" wrap>
            <Button type="submit" size="sm" data-testid="life-event-form-submit" disabled={submitting}>
              {submitting ? 'Saving…' : isEditing ? 'Save changes' : 'Add event'}
            </Button>
            {submitting && <Spinner size="sm" aria-label="Saving this event" />}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              data-testid="life-event-form-cancel"
              onClick={onCancel}
              disabled={submitting}
            >
              Cancel
            </Button>
          </Inline>
        </Stack>
      </form>
    </Card>
  );
}

/** One event as a read row: its title, its date or date range, and its note
 *  when it has one — plus edit/delete, entirely absent rather than disabled
 *  when `readOnly`. */
function EventRow({
  event,
  readOnly,
  formOpen,
  deleteState,
  onEdit,
  onDeleteRequested,
  onDeleteConfirmed,
  onDeleteCancelled,
  deleteError,
}: {
  event: LifeEvent;
  readOnly: boolean;
  formOpen: boolean;
  deleteState: DeleteState;
  onEdit: () => void;
  onDeleteRequested: () => void;
  onDeleteConfirmed: () => void;
  onDeleteCancelled: () => void;
  deleteError: string | null;
}) {
  const confirming = deleteState?.id === event.id && deleteState.kind === 'confirm';
  const deleting = deleteState?.id === event.id && deleteState.kind === 'deleting';

  return (
    <Card data-testid="life-event-row">
      <Stack gap={2}>
        <Inline gap={3} align="center" wrap className={styles.rowHead}>
          <Stack gap={1} className={styles.rowMain}>
            <Inline gap={2} align="center" wrap>
              <Text weight="medium">{event.title}</Text>
              {event.category && (
                <Badge variant="neutral" data-testid="life-event-category">
                  {event.category}
                </Badge>
              )}
            </Inline>
            <Text size="sm" color="muted">{formatEventRange(event)}</Text>
          </Stack>

          {!readOnly && !confirming && !deleting && (
            <Inline gap={2}>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                data-testid="life-event-edit"
                onClick={onEdit}
                disabled={formOpen}
              >
                Edit
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                data-testid="life-event-delete-button"
                onClick={onDeleteRequested}
                disabled={formOpen}
              >
                Delete
              </Button>
            </Inline>
          )}
        </Inline>

        {event.note && <Text size="sm" color="secondary">{event.note}</Text>}

        {!readOnly && (confirming || deleting) && (
          <Inline gap={3} align="center" wrap data-testid="life-event-delete-confirm">
            <Text size="sm" color="secondary">
              Delete &ldquo;{event.title}&rdquo;? This can&rsquo;t be undone.
            </Text>
            <Button
              type="button"
              variant="danger"
              size="sm"
              data-testid="life-event-delete-confirm-button"
              onClick={onDeleteConfirmed}
              disabled={deleting}
            >
              {deleting ? 'Deleting…' : 'Yes, delete'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={deleting}
              onClick={onDeleteCancelled}
            >
              Cancel
            </Button>
          </Inline>
        )}

        {!readOnly && deleteState?.id === event.id && deleteError && (
          <Alert variant="error" title="Couldn't delete this event" data-testid="life-event-delete-error">
            {deleteError}
          </Alert>
        )}
      </Stack>
    </Card>
  );
}

/**
 * Lists a node's significant life events — moments and periods alike — and,
 * unless `readOnly`, lets the owner add, edit and delete them.
 *
 * Somebody else mounts this inside the node-detail screen; this component
 * only ever talks to `nodeId`'s own events.
 */
export function LifeEvents({ nodeId, readOnly, onChanged }: LifeEventsProps) {
  const [listState, setListState] = useState<ListState>({ kind: 'loading' });
  const [formState, setFormState] = useState<FormState>({ kind: 'closed' });
  const [deleteState, setDeleteState] = useState<DeleteState>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setListState({ kind: 'loading' });
    try {
      const events = await fetchNodeEvents(nodeId);
      setListState({ kind: 'loaded', events: sortEvents(events).reverse() });
    } catch (err) {
      setListState({ kind: 'error', message: errorMessage(err, 'Could not load these events.') });
    }
  }, [nodeId]);

  useEffect(() => {
    void load();
    // Closing whatever was open when the node itself changes — a form or a
    // delete confirmation left over from a different node's events would be
    // editing the wrong thing.
    setFormState({ kind: 'closed' });
    setDeleteState(null);
  }, [load]);

  function upsert(saved: LifeEvent) {
    setListState((prev) => {
      if (prev.kind !== 'loaded') return prev;
      const withoutSaved = prev.events.filter((e) => e.id !== saved.id);
      return { kind: 'loaded', events: sortEvents([...withoutSaved, saved]).reverse() };
    });
    setFormState({ kind: 'closed' });
    onChanged?.();
  }

  async function handleDelete(eventId: string) {
    setDeleteState({ id: eventId, kind: 'deleting' });
    setDeleteError(null);
    try {
      await deleteNodeEvent(nodeId, eventId);
      setListState((prev) =>
        prev.kind === 'loaded' ? { kind: 'loaded', events: prev.events.filter((e) => e.id !== eventId) } : prev,
      );
      setDeleteState(null);
      onChanged?.();
    } catch (err) {
      setDeleteState({ id: eventId, kind: 'confirm' });
      setDeleteError(errorMessage(err, 'Could not delete this event.'));
    }
  }

  const formOpen = formState.kind !== 'closed';
  // categorySuggestions already de-duplicates by normalized spelling (see
  // shared/src/nodes/events.ts) — this is the only place that list is built.
  const categoryOptions = categorySuggestions(listState.kind === 'loaded' ? listState.events : []);

  return (
    <Stack gap={4} className={styles.page} data-testid="life-events">
      <Inline gap={3} align="center" wrap>
        <Heading level={3} size="lg">Life events</Heading>
        {!readOnly && !formOpen && (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            data-testid="life-events-add"
            onClick={() => setFormState({ kind: 'create' })}
          >
            Add an event
          </Button>
        )}
      </Inline>

      {!readOnly && formState.kind === 'create' && (
        <EventForm
          event={null}
          categoryOptions={categoryOptions}
          createEvent={(input) => createNodeEvent(nodeId, input)}
          onSaved={upsert}
          onCancel={() => setFormState({ kind: 'closed' })}
        />
      )}

      {listState.kind === 'loading' && (
        <Inline gap={3} align="center" data-testid="life-events-loading">
          <Spinner size="sm" aria-label="Loading life events" />
          <Text size="sm" color="muted">Loading…</Text>
        </Inline>
      )}

      {listState.kind === 'error' && (
        <Alert variant="error" title="Couldn't load these events" data-testid="life-events-error">
          {listState.message}
        </Alert>
      )}

      {listState.kind === 'loaded' && listState.events.length === 0 && (
        <Text size="sm" color="muted" data-testid="life-events-empty">
          No life events recorded yet.
        </Text>
      )}

      {listState.kind === 'loaded' && listState.events.length > 0 && (
        <Stack gap={3} data-testid="life-events-list">
          {listState.events.map((event) =>
            !readOnly && formState.kind === 'edit' && formState.event.id === event.id ? (
              <EventForm
                key={event.id}
                event={formState.event}
                categoryOptions={categoryOptions}
                createEvent={(input) => updateNodeEvent(nodeId, event.id, input)}
                onSaved={upsert}
                onCancel={() => setFormState({ kind: 'closed' })}
              />
            ) : (
              <EventRow
                key={event.id}
                event={event}
                readOnly={readOnly}
                formOpen={formOpen}
                deleteState={deleteState}
                deleteError={deleteError}
                onEdit={() => setFormState({ kind: 'edit', event })}
                onDeleteRequested={() => {
                  setDeleteError(null);
                  setDeleteState({ id: event.id, kind: 'confirm' });
                }}
                onDeleteConfirmed={() => void handleDelete(event.id)}
                onDeleteCancelled={() => setDeleteState(null)}
              />
            ),
          )}
        </Stack>
      )}
    </Stack>
  );
}
